import React from 'react';

import { StyledSectionMessageWrapper, StyledTableData } from '../StyledComponents';
import SectionMessage from '@atlaskit/section-message';
import t from '../../../../i18n/intl';
import config from '../../../../../appconfig';

const pmspLink = (chunks) => (
  <a
    href={`${config.atlAdminUrl}/partner-managed-subscriptions`}
    target="_blank"
    rel="noopener noreferrer"
  >
    {chunks}
  </a>
);

export const billingConsoleLink = (chunks) => (
  <a href={`${config.adminHubUrl}/billing`} target="_blank" rel="noopener noreferrer">
    {chunks}
  </a>
);

const dataCenterBACLink = (chunks, txaId, ccpEntitlementId) => (
  <a
    href={`${config.atlAdminUrl}/billing/${txaId}/entitlement/${ccpEntitlementId}`}
    target="_blank"
    rel="noopener noreferrer"
  >
    {chunks}
  </a>
);

const renderMigratedSubscriptionBanner = (accountDetails) => {
  if (accountDetails?.merchandise?.productPackage?.isDataCenter === true && accountDetails?.slug) {
    return t(
      {
        id: 'mac.licenses.manage.dc.subscriptions.text',
        defaultMessage:
          'You can now manage this subscription as {slug} in <link>Atlassian Billing Console.</link>',
      },
      {
        link: (chunks) =>
          dataCenterBACLink(
            chunks,
            accountDetails?.transactionAccountId,
            accountDetails?.ccpEntitlementId,
          ),
        slug: <strong>{accountDetails?.slug}</strong>,
      },
    );
  }
  return t(
    {
      id: 'mac.licenses.manage.cloud.subscriptions.text',
      defaultMessage:
        'You can now manage your cloud subscriptions in <link>Atlassian Billing Console.</link>',
    },
    {
      link: (chunks: string) => billingConsoleLink(chunks),
    },
  );
};

const renderPartnerMigratedSubscriptionBanner = (accountDetails) => {
  if (accountDetails?.merchandise?.productPackage?.isDataCenter === true && accountDetails?.slug) {
    return t(
      {
        id: 'mac.licenses.see.partner.managed.details.data.center',
        defaultMessage:
          'See details for this subscription as {slug} at the <link>partner managed subscription portal</link>.',
      },
      {
        link: (chunks) => pmspLink(chunks),
        slug: <strong>{accountDetails?.slug}</strong>,
      },
    );
  }
  return t(
    {
      id: 'mac.licenses.see.partner.managed.details',
      defaultMessage:
        'See details for this subscription at the <link>partner managed subscription portal</link>.',
    },
    {
      link: (chunks: string) => pmspLink(chunks),
    },
  );
};

const MigratedSubscriptionBanner = ({ accountDetails, userOrgDetails }) => {
  const isPartnerManaged = accountDetails?.partnerManaged;
  const isDifferentPartner = userOrgDetails?.id !== accountDetails?.latestPeriod?.partnerId;

  return (
    <StyledTableData $paddingLeft colSpan={5}>
      <StyledSectionMessageWrapper>
        <SectionMessage appearance="information">
          {isPartnerManaged && isDifferentPartner
            ? renderPartnerMigratedSubscriptionBanner(accountDetails)
            : renderMigratedSubscriptionBanner(accountDetails)}
        </SectionMessage>
      </StyledSectionMessageWrapper>
    </StyledTableData>
  );
};

const renderMigratedCloudSubscriptionBanner = () =>
  t(
    {
      id: 'mac.licenses.manage.cloud.subscriptions.text',
      defaultMessage:
        'You can now manage your cloud subscriptions in <link>Atlassian Billing Console.</link>',
    },
    {
      link: (chunks: string) => billingConsoleLink(chunks),
    },
  );

const renderPartnerMigratedCloudSubscriptionBanner = () =>
  t(
    {
      id: 'mac.licenses.see.partner.managed.details.cloud',
      defaultMessage:
        'You can now manage your cloud subscriptions in <link>Partner Purchasing Center</link>.',
    },
    {
      link: (chunks: string) => pmspLink(chunks),
    },
  );

export const MigratedCloudSubscriptionsBanner = ({ shouldShowPPCLink }) => (
  <SectionMessage appearance="information">
    {shouldShowPPCLink
      ? renderPartnerMigratedCloudSubscriptionBanner()
      : renderMigratedCloudSubscriptionBanner()}
  </SectionMessage>
);

export default MigratedSubscriptionBanner;
